import { useMemo } from 'react';
import { getPercentageByTotal } from '../utilis/common';
import { PAGE_TYPE } from '../types/pages.type';

/**
 * Custom hook for AI facility analysis and multi-facility data
 * Provides comprehensive facility-wise breakdown for answering multi-facility questions
 * IMPORTANT: Page-aware - calculates different metrics based on page type
 */
export const useAIFacilityContext = ({
  activeFacilities,
  patientList,
  facilities,
  dbData,
  percentageBy,
  lockedTotal,
  lockedTotalBy,
  pageType // CRITICAL: Added to determine which metrics to calculate
}) => {
  return useMemo(() => {
    // Determine page type once for the entire hook
    const isHospitalPage = pageType === PAGE_TYPE.HOSPITAL;
    const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
    const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
    
    // CRITICAL: Comprehensive Facility Analysis for Multi-Facility Questions
    const facilityAnalysis = {
      description: "Complete facility-wise breakdown for answering multi-facility questions",
      
      // Active facilities with detailed metrics
      activeFacilities: activeFacilities?.map(facilityId => {
        const facilityPatients = patientList?.list?.filter(patient => patient.facilityId === facilityId) || [];
        
        // Get facility name from Redux state instead of patient data
        const facilityFromState = facilities?.find(facility => facility._id === facilityId || facility.id === facilityId);
        const facilityName = facilityFromState?.name || facilityFromState?.facilityName || `Healthcare Facility ${facilityId.slice(-4)}`;
        
        // Calculate facility-specific metrics
        const totalTransfers = facilityPatients.length;
        
        // Hospital-only metrics
        const plannedTransfers = isHospitalPage ? facilityPatients.filter(p => p.transferType === 'plannedHospitalTransfer').length : 0;
        const unplannedTransfers = isHospitalPage ? facilityPatients.filter(p => p.transferType === 'unplannedHospitalTransfer').length : 0;
        const dcCount = isHospitalPage ? facilityPatients.filter(p => p.wasAdmitted === true).length : 0;
        const erCount = isHospitalPage ? facilityPatients.filter(p => p.wasAdmitted === false).length : 0;
        const newHospitalizations = isHospitalPage ? facilityPatients.filter(p => p.reHospitalization === false).length : 0;
        const reHospitalizations = isHospitalPage ? facilityPatients.filter(p => p.reHospitalization === true).length : 0;
        
        // Community Transfer-only metrics
        const safeDischargeCount = isCommunityPage ? facilityPatients.filter(p => p.isSafeDischarge === true || p.transferType === 'safeDischarge').length : 0;
        const snfCount = isCommunityPage ? facilityPatients.filter(p => p.transferType === 'SNF' || p.isSNF === true).length : 0;
        const amaCount = isCommunityPage ? facilityPatients.filter(p => p.transferType === 'AMA' || p.isAMA === true).length : 0;
        
        // Hospital and Community metrics (NOT Deceased)
        const returnedCount = !isDeceasedPage ? facilityPatients.filter(p => p.wasReturned === true).length : 0;
        const didntReturnCount = !isDeceasedPage ? facilityPatients.filter(p => p.wasReturned === false).length : 0;
        
        // Get facility's percentage base (census/bed capacity)
        const facilityPercentageBase = getPercentageByTotal(dbData);
        
        // Build metrics object conditionally based on page type
        const baseMetrics = {
          id: facilityId,
          name: facilityName,
          address: facilityFromState?.address || null,
          
          // Core transfer/deceased metrics (all pages)
          totalTransfers,
          
          // Percentage against facility baseline
          percentageAgainstBaseline: facilityPercentageBase > 0 ? ((totalTransfers / facilityPercentageBase) * 100).toFixed(1) : 0,
          
          // Top categories for this facility (all pages)
          topInsurance: facilityPatients.reduce((acc, patient) => {
            const insurance = patient.insurance?.name || 'Unknown';
            acc[insurance] = (acc[insurance] || 0) + 1;
            return acc;
          }, {}),
          
          topDiagnoses: isHospitalPage ? facilityPatients.reduce((acc, patient) => {
            const diagnosis = patient.diagnosis?.description || 'Unknown';
            acc[diagnosis] = (acc[diagnosis] || 0) + 1;
            return acc;
          }, {}) : {},
          
          topFloors: facilityPatients.reduce((acc, patient) => {
            const floor = patient.floor?.name || 'Unknown';
            acc[floor] = (acc[floor] || 0) + 1;
            return acc;
          }, {}),
          
          // Shift distribution (Hospital only)
          ...(isHospitalPage && {
            shiftDistribution: {
              morning: facilityPatients.filter(p => p.shiftName === 'Morning').length,
              evening: facilityPatients.filter(p => p.shiftName === 'Evening').length,
              night: facilityPatients.filter(p => p.shiftName === 'Night').length
            }
          })
        };
        
        // Add Hospital-specific metrics
        const hospitalMetrics = isHospitalPage ? {
          plannedTransfers,
          unplannedTransfers,
          plannedPercentage: totalTransfers > 0 ? ((plannedTransfers / totalTransfers) * 100).toFixed(1) : 0,
          unplannedPercentage: totalTransfers > 0 ? ((unplannedTransfers / totalTransfers) * 100).toFixed(1) : 0,
          dcCount,
          erCount,
          dcPercentage: totalTransfers > 0 ? ((dcCount / totalTransfers) * 100).toFixed(1) : 0,
          erPercentage: totalTransfers > 0 ? ((erCount / totalTransfers) * 100).toFixed(1) : 0,
          admissionRate: totalTransfers > 0 ? ((dcCount / totalTransfers) * 100).toFixed(1) : 0,
          newHospitalizations,
          reHospitalizations,
          reHospitalizationRate: (newHospitalizations + reHospitalizations) > 0 ? ((reHospitalizations / (newHospitalizations + reHospitalizations)) * 100).toFixed(1) : 0
        } : {};
        
        // Add Community Transfer-specific metrics
        const communityMetrics = isCommunityPage ? {
          safeDischargeCount,
          snfCount,
          amaCount,
          safeDischargePercentage: totalTransfers > 0 ? ((safeDischargeCount / totalTransfers) * 100).toFixed(1) : 0,
          snfPercentage: totalTransfers > 0 ? ((snfCount / totalTransfers) * 100).toFixed(1) : 0,
          amaPercentage: totalTransfers > 0 ? ((amaCount / totalTransfers) * 100).toFixed(1) : 0,
          // Community transfer type distribution
          transferTypeDistribution: {
            safeDischarge: safeDischargeCount,
            snf: snfCount,
            ama: amaCount
          }
        } : {};
        
        // Add Hospital and Community metrics (NOT Deceased)
        const returnMetrics = !isDeceasedPage ? {
          returnedCount,
          didntReturnCount,
          returnRate: (returnedCount + didntReturnCount) > 0 ? ((returnedCount / (returnedCount + didntReturnCount)) * 100).toFixed(1) : 0
        } : {};
        
        return {
          ...baseMetrics,
          ...hospitalMetrics,
          ...communityMetrics,
          ...returnMetrics
        };
      }) || [],
      
      // Facility percentage calculation methodology
      percentageCalculationMethod: {
        description: "How 'Against Each Facilities Own' percentages are calculated",
        methodology: `Each facility's transfers are calculated as a percentage of their own baseline:
            1. Baseline Determination: Uses ${percentageBy || 'census/bed capacity'} for each facility
            2. Individual Calculation: (Facility Transfers / Facility Baseline) × 100
            3. This allows fair comparison between facilities of different sizes
            4. Example: Small facility (50 beds) with 10 transfers = 20%, Large facility (200 beds) with 30 transfers = 15%`,
        
        currentMethod: percentageBy || 'bedCapacity',
        isLocked: !!lockedTotal,
        lockedBy: lockedTotalBy,
        
        baselineExplanation: {
          bedCapacity: "Uses each facility's total bed capacity as the denominator",
          censusAverage: "Uses each facility's average census/occupancy as the denominator", 
          censusAsOfNow: "Uses each facility's current census as the denominator",
          lockedTotal: lockedTotal ? "Uses a custom locked total as the denominator" : null
        },
        
        currentBaseline: percentageBy
      },
      
      // Cross-facility insights
      crossFacilityInsights: {
        totalFacilities: activeFacilities?.length || 0,
        facilitiesWithTransfers: activeFacilities?.filter(facilityId => {
          const facilityPatients = patientList?.list?.filter(patient => patient.facilityId === facilityId) || [];
          return facilityPatients.length > 0;
        }).length || 0,
        
        averageTransfersPerFacility: activeFacilities?.length > 0 ? 
          ((patientList?.list?.length || 0) / activeFacilities.length).toFixed(1) : 0
      },
      
      // Common facility questions AI should be able to answer (page-aware)
      commonQuestions: (() => {
        const baseQuestions = [
          "Which facility has the most transfers?",
          "Which facility has the highest percentage against their baseline?",
          "Show me the top 3 facilities by volume",
          "What's the average number per facility?",
          "How are they distributed across all facilities?",
          "Which days are busiest for each facility?",
          "What are the top insurance types by facility?",
          "Show me floor breakdown by facility?"
        ];
        
        const hospitalQuestions = isHospitalPage ? [
          "What's the admission rate for each facility?",
          "Which facilities have the most planned vs unplanned transfers?",
          "Which facility has the most DC vs ER transfers?",
          "What's the re-hospitalization rate by facility?",
          "Show me facility-wise shift patterns",
          "Which facilities have the most weekend transfers?",
          "How do morning shift transfers compare across facilities?"
        ] : [];
        
        const communityQuestions = isCommunityPage ? [
          "Which facility has the most safe discharge transfers?",
          "How do SNF transfers compare across facilities?",
          "What's the AMA rate for each facility?",
          "Show me Safe Discharge AL breakdown by facility",
          "Which facilities have the most SNF facility transfers?",
          "How do transfer types distribute across facilities?"
        ] : [];
        
        const deceasedQuestions = isDeceasedPage ? [
          "Which facility has the most deceased residents?",
          "What's the mortality rate for each facility?",
          "How do facilities compare in terms of deceased residents?",
          "Show me deceased residents by facility",
          "Which facility has the highest mortality percentage?"
        ] : [];
        
        return [...baseQuestions, ...hospitalQuestions, ...communityQuestions, ...deceasedQuestions];
      })()
    };

    return {
      facilityAnalysis
    };
  }, [
    activeFacilities,
    patientList,
    facilities,
    dbData,
    percentageBy,
    lockedTotal,
    lockedTotalBy,
    pageType
  ]);
};

export default useAIFacilityContext;
